import React, { useState, useEffect } from "react";
import {
  getHealthySwap,
  initAuth,
  signupAndLink,
  upgradeWithGoogle,
  logout,
  signinWithEmail,
  signinWithGoogle,
  getSwapUsage,
  migrateSwaps,
  getUserProfile,
  saveUserProfile,
  addFeedback,
  auth,
  db
} from "./firebase";

import { doc, getDoc, setDoc } from "firebase/firestore";
import { sendEmailVerification, fetchSignInMethodsForEmail } from "firebase/auth";
import { ToastContainer } from "react-toastify";
import { FaFacebook, FaXTwitter, FaLinkedin, FaWhatsapp } from "react-icons/fa6";
import { HiOutlineMail } from "react-icons/hi";
import "react-toastify/dist/ReactToastify.css";

import { foodListFromFile } from "./data/foodList";
import UsageDashboard from "./components/UsageDashboard";
import SwapForm from "./components/SwapForm";
import AuthPanel from "./components/AuthPanel";
import SwapResult from "./components/SwapResult";
import UserProfile from "./components/UserProfile";
import AdminPanel from "./components/AdminPanel";
import FeedbackModal from "./components/FeedbackModal";
import { getAuthErrorMessage } from "./utils/getAuthErrorMessage";
import { useToast } from "./utils/useToast";
import { sendWelcomeEmail } from "./firebase"; // your callable wrapper
import QRCodeModal from "./components/QRCodeModal";
import { useIsMobile } from "./utils/useIsMobile";
// eslint-disable-next-line
import AdSlot from "./components/AdSlot";
import { ADS_CONFIG } from "./config/ADS_CONFIG";

import "./App.css";


//  CONSTANTS
const foodListHardcoded = ["white bread", "3% milk", "rice", "soda pop", "orange juice"];
const foodListURL = "https://firebasestorage.googleapis.com/v0/b/mbsl001.firebasestorage.app/o/foodList_test1.txt?alt=media";
const DATA_SOURCE = "storage"; // "hardcoded", "file", or "storage"

const shareUrl = "https://mybloodsugarlevel.com/healthy-swaps";
const shareText = encodeURIComponent("Check out this Healthy Swaps tool to find healthier food alternatives!");

function App() {
  const [user, setUser] = useState(null);
  const [foodList, setFoodList] = useState([]);
  const [selectedFood, setSelectedFood] = useState(() => { return localStorage.getItem("lastSelectedFood") || ""; });
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [usage, setUsage] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authMode, setAuthMode] = useState("signup");
  const [authError, setAuthError] = useState("");
  const [condition, setCondition] = useState("Type 2 diabetes"); // default condition
  const [swapOptions, setSwapOptions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [showAnotherVisible, setShowAnotherVisible] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [qrOpen, setQROpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("signup"); // or "signin"
  const [swapComment, setSwapComment] = useState(null);
  const [isConditionFriendly, setIsConditionFriendly] = useState(null);
  const [debugAds, setDebugAds] = useState(ADS_CONFIG.debugAds);
  const [selectedSwapIndex, setSelectedSwapIndex] = useState(0);

  // eslint-disable-next-line
  const isMobile = useIsMobile();

  useEffect(() => {
    initAuth().then(setUser).catch(console.error);
  }, []);

  useEffect(() => {
    setDebugAds(ADS_CONFIG.debugAds);
  }, []);

  useEffect(() => {
    if (selectedFood) {
      localStorage.setItem("lastSelectedFood", selectedFood);
    } else {
      localStorage.removeItem("lastSelectedFood");
    }
  }, [selectedFood]);

  useEffect(() => {
    if (DATA_SOURCE === "hardcoded") {
      setFoodList(foodListHardcoded);
      setSelectedFood(foodListHardcoded[0]);
    } else if (DATA_SOURCE === "file") {
      setFoodList(foodListFromFile);
      setSelectedFood(foodListFromFile[0]);
    } else if (DATA_SOURCE === "storage") {
      fetch(foodListURL)
        .then((res) => res.text()) // 👈 parse as plain text
        .then((data) => {
          const lines = data
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
          setFoodList(lines);
          setSelectedFood(lines[0] || "");
        })
        .catch((err) => console.error("Failed to load food list from storage", err));
    }
  }, []);

  useEffect(() => {
    if (user && user.isAnonymous) {
      getSwapUsage({ uid: user.uid, isAnonymous: true })
        .then(res => setUsage(res.data))
        .catch(err => console.error("Usage fetch error:", err));
    }
  }, [user]);

  useEffect(() => {
    const fetchCondition = async () => {
      if (user && !user.isAnonymous) {
        try {
          const profile = await getUserProfile(user.uid);
          setCondition(profile?.condition || "Type 2 diabetes");
        } catch (err) {
          console.error("Failed to fetch user condition:", err);
        }
      }
    };
    fetchCondition();
  }, [user]);

  useEffect(() => {
    if (user) {
      user.getIdTokenResult().then((idTokenResult) => {
        setIsAdmin(idTokenResult.claims.admin === true);
      });
    }
  }, [user]);

  const { showToast } = useToast();

  const pickAnotherSwap = () => {
    if (swapOptions.length > 1) {
      const nextIndex = (selectedSwapIndex + 1) % swapOptions.length;
      setSelectedSwap(swapOptions[nextIndex]);
      setSelectedSwapIndex(nextIndex);
  
      setShowAnotherVisible(false);
      setTimeout(() => {
        setShowAnotherVisible(true);
      }, 1500);
    }
  };

  const fetchSwap = async () => {
    if (!user) return;
    setLoading(true);
    setSelectedSwap(null);
    setSwapOptions([]);

    try {
      const result = await getHealthySwap({
        food: selectedFood,
        uid: user.uid,
        isAnonymous: user.isAnonymous
      });
      const { swapOptions, swapComment, limitReached, isConditionFriendly } = result.data;

      if (limitReached) {
        setLimitReached(true);
        setSwapOptions([
          {
            swap: "⚠️ You've reached your swap limit.",
            explanation: "You've used 2 swaps today. Sign up FREE for unlimited swaps and personalized suggestions."
          }
        ]);
      } else {
        const options = swapOptions || [];
        setSwapOptions(options);
        setSelectedSwapIndex(0);
        setSelectedSwap(options[0] || null);
        setSwapComment(swapComment || null);
        setIsConditionFriendly(isConditionFriendly || null);
        setLimitReached(false);
      }
      if (user.isAnonymous) {
        const usageRes = await getSwapUsage({ uid: user.uid, isAnonymous: true });
        setUsage(usageRes.data);
      }
    } catch (error) {
      setSwapOptions([
        {
          swap: "❌ An error occurred.",
          explanation: error.message || "Please try again later."
        }
      ]);
    }
    setLoading(false);
  };

  const renderAuthUI = () => {
    if (!user || !user.isAnonymous) return null;

    const handleSignup = async () => {
      setAuthLoading(true);
      try {
        const upgradedUser = await signupAndLink(email, password);
        setUser(upgradedUser);
        await saveUserProfile({
          uid: upgradedUser.uid,
          email: upgradedUser.email,
          condition,
        });
        setLimitReached(false);

        // ✅ Send verification email
        await sendEmailVerification(upgradedUser);

        // showToast("Signed up successfully! 🎉");
        showToast("A verification email has been sent. Please check your inbox.");

        setAuthError("");
      } catch (err) {
        if (err.message.includes("auth/email-already-in-use")) {
          setAuthMode("signin");
          try {
            const methods = await fetchSignInMethodsForEmail(auth, email);
            if (methods.includes("google.com")) {
              showToast("This email is registered with Google. Please use 'Sign in with Google' instead.", "error");
            } else if (methods.includes("password")) {
              showToast(getAuthErrorMessage(err.code, "signup"), "error");
            } else {
              showToast("This email is already in use. Please try signing in.", "error");
            }
          } catch (checkErr) {
            showToast("Something went wrong checking this email.", "error");
          }
        } else {
          setAuthError("Signup failed: " + err.message);
          showToast("Signup failed: " + err.message, "error");
        }
      }
      setAuthLoading(false);
    };

    const handleSignin = async () => {
      setAuthLoading(true);

      try {
        const signedInUser = await signinWithEmail(email, password);

        if (signedInUser) {
          // ✅ Reload user to get latest emailVerified status
          await signedInUser.reload();
          const updatedUser = auth.currentUser;

          setEmailVerified(updatedUser.emailVerified);
          setUser(updatedUser);
          setLimitReached(false);
          setAuthError("");

          // ✅ Send welcome email if verified and not already sent
          if (updatedUser.emailVerified) {
            const userDocRef = doc(db, "users", updatedUser.uid);
            const userDocSnap = await getDoc(userDocRef);

            const alreadyWelcomed = userDocSnap.exists() && userDocSnap.data().welcomeEmailSent;

            if (!alreadyWelcomed) {
              try {
                await sendWelcomeEmail({
                  email: updatedUser.email,
                  name: updatedUser.displayName || "",
                  uid: updatedUser.uid
                });

                await setDoc(userDocRef, { welcomeEmailSent: true }, { merge: true });
                // showToast("Welcome email sent!", "success");
              } catch (err) {
                //TODO send error log to backend.
                console.log(`Error sending welcome email: ${err.message}`);
              }
            }
          }
        }
      } catch (err) {
        const msg = getAuthErrorMessage(err.code, "signin");
        setAuthError(msg);
        showToast(msg, "error");
      }

      setAuthLoading(false);
    };

    const handleGoogleAuth = async () => {
      setAuthLoading(true);
      try {
        const result = authMode === "signup"
          ? await upgradeWithGoogle()
          : await signinWithGoogle();
        setUser(result);
        setLimitReached(false);
        // showToast("Signed in with Google successfully! 🎉");
        setAuthError("");
      } catch (err) {
        if (err.message.includes("already in use")) {
          setAuthMode("signin");
          const msg = "That Google account is already in use. Please sign in instead.";
          setAuthError(msg);
          showToast(msg, "error");
        } else {
          setAuthError("Google sign-in failed: " + err.message);
          showToast("Google sign-in failed: " + err.message, "error");
        }
      }
      setAuthLoading(false);
    };

    return (
      <>
        {limitReached && (
          <div style={{ color: "red", marginBottom: "1rem", fontWeight: "bold" }}>
            ⚠️ You’ve reached your free daily usage limit.{" "}
            <span style={{ color: "red", fontWeight: "bold" }}>FREE</span>{" "}
            <span
              onClick={() => {
                setAuthModalMode("signup");
                setAuthModalOpen(true);
              }}
              style={{
                color: "#007bff",
                textDecoration: "underline",
                cursor: "pointer",
                fontWeight: "bold"
              }}
            >
              sign up
            </span>{" "}
            for unlimited swaps.
          </div>
        )}
        <div id="auth-panel">
          {authModalOpen && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1000,
                display: "flex",
                justifyContent: "center",
                alignItems: "center"
              }}
              onClick={() => setAuthModalOpen(false)}
            >
              <div
                style={{ background: "#fff", padding: "2rem", borderRadius: "8px", maxWidth: "500px", width: "100%", position: "relative" }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* ❌ Close Button */}
                <button
                  onClick={() => setAuthModalOpen(false)}
                  style={{
                    position: "absolute",
                    top: "0.5rem",
                    right: "0.5rem",
                    background: "transparent",
                    border: "none",
                    fontSize: "1.2rem",
                    cursor: "pointer"
                  }}
                  aria-label="Close"
                >
                  ❌
                </button>

                <AuthPanel
                  email={email}
                  password={password}
                  authMode={authModalMode}
                  authLoading={authLoading}
                  authError={authError}
                  setEmail={setEmail}
                  setPassword={setPassword}
                  setAuthMode={setAuthModalMode}
                  onSignup={async () => {
                    await handleSignup();
                    setAuthModalOpen(false);
                  }}
                  onSignin={async () => {
                    await handleSignin();
                    setAuthModalOpen(false);
                  }}
                  onGoogleAuth={async () => {
                    await handleGoogleAuth();
                    setAuthModalOpen(false);
                  }}
                  condition={condition}
                  setCondition={setCondition}
                />

                {authModalMode === "signup" && (
                  <div style={{ textAlign: "center", marginTop: "1rem" }}>
                    Already have an account?{" "}
                    <span
                      onClick={() => setAuthModalMode("signin")}
                      style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
                    >
                      Sign in
                    </span>
                  </div>
                )}

              </div>
            </div>
          )}

        </div>
      </>
    );
  };

  const buildUTMLink = (baseUrl, source, medium, campaign) => {
    const url = new URL(baseUrl);
    url.searchParams.set("utm_source", source);
    url.searchParams.set("utm_medium", medium);
    url.searchParams.set("utm_campaign", campaign);
    return url.toString();
  };

  const handleShareResult = async () => {
    const shareUrl = buildUTMLink(
      "https://mybloodsugarlevel.com/healthy-swaps",
      "user_share",
      "swap_result",
      "healthy_swaps"
    );

    const shareText = `💡 Healthy Swap for "${selectedFood}":
  👉 ${selectedSwap?.swap}
  🧠 Why: ${selectedSwap?.explanation}
  
  Try it yourself 👉 ${shareUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Healthy Food Swap",
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        console.warn("Sharing cancelled or failed:", err);
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      showToast("Swap copied! Share it anywhere.", "success");
    }
  };

  const handleShareTool = async () => {
    const shareUrl = buildUTMLink(
      "https://mybloodsugarlevel.com/healthy-swaps",
      "user_share",
      "tool",
      "healthy_swaps"
    );

    const message = `🎯 Check out this Healthy Swaps tool! Find better food alternatives for diabetes and more.
  
  Try it 👉 ${shareUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Try the Healthy Swaps Tool",
          text: message,
          url: shareUrl,
        });
      } catch (err) {
        console.warn("Tool share cancelled or failed:", err);
      }
    } else {
      await navigator.clipboard.writeText(message);
      showToast("Link copied to clipboard!", "success");
    }
  };

  return (
    <div style={{ padding: "2rem", fontFamily: "Arial", maxWidth: 600, margin: "auto" }}>
      <ToastContainer />
      {/* <h2>Healthy Food Swaps</h2> */}
      {/* <AdSlot type="banner" user={user} /> */}

      {user && !user.isAnonymous && (
        <div>


          <div style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "1rem" }}>
            <span>Signed in as <strong>{user.email}</strong></span>
            <button onClick={async () => {
              await logout();
              const newUser = await initAuth();
              console.log("New anonymous user UID after logout:", newUser.uid);
              setUser(newUser);
              setSwapOptions([]);
              setLimitReached(true);
              setSelectedSwap(null);
              setSelectedFood("Search foods...");

              const oldAnonUid = localStorage.getItem("anonUid");
              const newAnonUid = newUser.uid;

              if (oldAnonUid && oldAnonUid !== newAnonUid) {
                try {
                  // Call backend function to migrate swap data
                  await migrateSwaps(oldAnonUid, newAnonUid);
                  localStorage.setItem("anonUid", newAnonUid); // Update for future sessions
                  showToast("Your previous swap history has been restored.");
                } catch (err) {
                  console.error("Migration error:", err);
                }
              }

              try {
                const usageRes = await getSwapUsage({
                  uid: newAnonUid,
                  isAnonymous: true
                });
                setUsage(usageRes.data);
              } catch (err) {
                console.error("Usage fetch error after logout:", err);
                setUsage(null);
              }
            }}>
              Log Out
            </button>

          </div>
        </div>
      )}

      {(!user || user.isAnonymous) && (
        <>
          <p style={{ margin: "1rem 0rem 1rem 0rem", fontStyle: "italic", color: "#555" }}>
            <span style={{ color: "red", fontWeight: "bold" }}>FREE</span>{" "}
            <span
              onClick={() => {
                setAuthModalMode("signup");
                setAuthModalOpen(true);
              }}
              style={{ color: "#007bff", textDecoration: "underline", cursor: "pointer", fontWeight: "bold" }}
            >
              Sign up
            </span>{" "}
            for unlimited swaps. Otherwise 2 per day. Already have an account?{" "}
            <span
              onClick={() => {
                setAuthModalMode("signin");
                setAuthModalOpen(true);
              }}
              style={{ color: "#007bff", textDecoration: "underline", cursor: "pointer", fontWeight: "bold" }}
            >
              Sign in
            </span>
          </p>

        </>
      )}

      {/* {user && !user.isAnonymous && condition && (
        <div style={{ margin: "0.5rem 0rem 0.5rem 0rem", fontStyle: "italic", color: "#555" }}>
          Managing condition: <strong>{condition}</strong>
        </div>
      )} */}

      {user && !user.isAnonymous && !emailVerified && (
        <div style={{ margin: "1rem 0", padding: "1rem", background: "#fff3cd", border: "1px solid #ffeeba", borderRadius: "6px" }}>
          <strong>🔒 Please verify your email</strong><br />
          Check your inbox and click the verification link to unlock this feature.
          <br />
          <br />
          <button onClick={async () => {
            await sendEmailVerification(user);
            showToast("Verification email sent!", "success");
          }}>
            Resend Verification Email
          </button>
        </div>
      )}

      {/* eslint-disable-next-line */}
      {user && (!user.isAnonymous && emailVerified || user?.isAnonymous) && (
        <SwapForm
          foodList={foodList}
          selectedFood={selectedFood}
          onFoodChange={
            (value) => {
              setSelectedFood(value);
              setSelectedSwap(null); // ✅ Clear previous result immediately
            }
          }
          onFetchSwap={fetchSwap}
          loading={loading}
          limitReached={limitReached}
        />
      )}

      <div style={{ marginTop: "1rem" }}>
        <SwapResult
          swapOption={selectedSwap}
          swapComment={swapComment}
          isConditionFriendly={isConditionFriendly}
          food={selectedFood}
          user={user}
        />
      </div>

      {selectedSwap && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginTop: "1rem",
            alignItems: "center"
          }}
        >
          {swapOptions.length > 1 && (
            <button
              onClick={pickAnotherSwap}
              style={{
                opacity: showAnotherVisible ? 1 : 0,
                pointerEvents: showAnotherVisible ? "auto" : "none",
                transition: showAnotherVisible ? "opacity 1s ease" : "none"
              }}
            >
              ♻️ Show Another
            </button>
          )}

          <button onClick={handleShareResult}>🔗 Share this Swap</button>
          <button onClick={handleShareTool}>📢 Share this Tool</button>

          <button onClick={() => setShowFeedback(true)}>
            💬 Feedback
          </button>
        </div>
      )}

      <FeedbackModal
        open={showFeedback}
        onClose={() => setShowFeedback(false)}
        onSubmit={async (feedbackText) => {
          await addFeedback({
            food: selectedFood,
            swap: selectedSwap?.swap,
            explanation: selectedSwap?.explanation,
            feedback: feedbackText,
            user
          });
        }}
        food={selectedFood}
        swap={selectedSwap?.swap}
        explanation={selectedSwap?.explanation}
      />

      {renderAuthUI()}

      {user && user.isAnonymous && usage && (
        <UsageDashboard usage={usage} isAnonymous={true} />
      )}

      {user && !user.isAnonymous && (
        <UserProfile user={user} condition={condition} setCondition={setCondition} />
      )}

      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <p>💡 Help others eat healthier too!</p>
        <button onClick={handleShareTool}>📢 Share this Healthy Swaps Tool</button>
      </div>

      {/* <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <button
          onClick={() => setQROpen(true)}
        >
          📷 Show QR Code
        </button>
      </div> */}

      <QRCodeModal
        open={qrOpen}
        onClose={() => setQROpen(false)}
        url={buildUTMLink("https://mybloodsugarlevel.com/healthy-swaps", "qr", "offline", "healthy_swaps")}
      />

      {/* {!isMobile && <AdSlot type="sidebar" user={user} />} */}

      {isAdmin && (
        <div style={{ marginTop: "2rem" }}>
          <button
            onClick={() => setAdminPanelOpen((prev) => !prev)}
            style={{
              background: "#eee",
              border: "1px solid #ccc",
              padding: "0.5rem 1rem",
              fontSize: "1rem",
              borderRadius: "6px",
              cursor: "pointer"
            }}
          >
            {adminPanelOpen ? "▲ Hide Admin Panel" : "▼ Show Admin Panel"}
          </button>
          {adminPanelOpen && <AdminPanel user={user} />}
        </div>
      )}



      <div style={{ textAlign: "center", marginTop: "2rem", paddingBottom: "1rem" }}>
        <p style={{ fontWeight: "bold", marginBottom: "0.5rem" }}>Share this tool:</p>
        <div style={{ display: "flex", justifyContent: "center", gap: "1.5rem", flexWrap: "wrap" }}>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Share on Facebook"
            style={{ fontSize: "1.6rem", color: "#1877f2" }}
          >
            <FaFacebook />
          </a>

          <a
            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareText}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Share on X / Twitter"
            style={{ fontSize: "1.6rem", color: "#000" }}
          >
            <FaXTwitter />
          </a>

          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Share on LinkedIn"
            style={{ fontSize: "1.6rem", color: "#0a66c2" }}
          >
            <FaLinkedin />
          </a>

          <a
            href={`mailto:?subject=Check out this Healthy Swaps tool&body=${shareText}%20${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Share by Email"
            style={{ fontSize: "1.6rem", color: "#333" }}
          >
            <HiOutlineMail />
          </a>

          <a
            href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Share on WhatsApp"
            style={{ fontSize: "1.6rem", color: "#25D366" }}
          >
            <FaWhatsapp />
          </a>
        </div>
      </div>

      {/* <AdSlot type="inline" user={user} /> */}


      {process.env.NODE_ENV !== "production" && (
        <div
          style={{
            position: "fixed",
            bottom: "1rem",
            right: "1rem",
            backgroundColor: "#333",
            color: "white",
            padding: "0.5rem 1rem",
            borderRadius: "6px",
            zIndex: 9999,
            fontSize: "0.9rem",
            cursor: "pointer"
          }}
          onClick={() => {
            const newVal = !debugAds;
            ADS_CONFIG.setDebugAds(newVal);
            setDebugAds(newVal); // ✅ force re-render
            console.log(`🔁 Toggled debugAds: ${newVal}`);
          }}
        >
          {debugAds ? "🧪 Debug Ads: ON" : "🧪 Debug Ads: OFF"}
        </div>
      )}




    </div>
  );
}

export default App;
